import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';

const RenterProfileRatings = ({ renterId, compact = false }) => {
  const [ratings, setRatings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    fetchRenterRatings();
  }, [renterId]);

  const fetchRenterRatings = async () => {
    if (!renterId) return;

    setLoading(true);
    try {
      const { data, error } = await supabase?.from('renter_ratings')?.select('*')?.eq('renter_id', renterId)?.order('created_at', { ascending: false });

      if (error) throw error;
      setRatings(data || []);
    } catch (error) {
      console.error('Error fetching renter ratings:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderStars = (rating) => {
    return (
      <div className="flex items-center space-x-0.5">
        {[1, 2, 3, 4, 5]?.map((star) => (
          <Icon
            key={star}
            name="Star"
            size={14}
            strokeWidth={2}
            className={star <= rating ? 'text-accent fill-accent' : 'text-muted-foreground'}
          />
        ))}
      </div>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const calculateAverageRating = () => {
    if (ratings?.length === 0) return 0;
    const sum = ratings?.reduce((acc, r) => acc + r?.overall_rating, 0);
    return (sum / ratings?.length)?.toFixed(1);
  };

  const getRatingColor = (rating) => {
    if (rating >= 4.5) return 'text-success';
    if (rating >= 3.5) return 'text-warning';
    return 'text-error';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-4">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (ratings?.length === 0) {
    return (
      <div className="bg-muted rounded-lg p-4 text-center">
        <Icon name="Star" size={32} className="text-muted-foreground mx-auto mb-2" strokeWidth={1.5} />
        <p className="font-caption text-sm text-muted-foreground">
          No ratings yet
        </p>
      </div>
    );
  }

  const averageRating = calculateAverageRating();
  const displayRatings = compact && !showAll ? ratings?.slice(0, 3) : ratings;

  if (compact) {
    return (
      <div className="space-y-3">
        {/* Summary */}
        <div className="flex items-center space-x-3">
          <div className="flex items-center space-x-2">
            <span className={`font-heading text-2xl font-bold ${getRatingColor(averageRating)}`}>
              {averageRating}
            </span>
            {renderStars(Math.round(averageRating))}
          </div>
          <span className="font-caption text-sm text-muted-foreground">
            ({ratings?.length} {ratings?.length === 1 ? 'rating' : 'ratings'})
          </span>
        </div>

        {/* Recent Ratings */}
        <div className="space-y-2">
          {displayRatings?.map((rating) => (
            <div key={rating?.id} className="bg-muted rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2">
                  {renderStars(rating?.overall_rating)}
                  <span className="font-caption text-xs text-muted-foreground">
                    {formatDate(rating?.created_at)}
                  </span>
                </div>
              </div>
              {rating?.comment && (
                <p className="font-body text-sm text-foreground line-clamp-2">
                  {rating?.comment}
                </p>
              )}
              {rating?.category_ratings && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {Object.entries(rating?.category_ratings)?.map(([key, value]) => (
                    value > 0 && (
                      <div key={key} className="flex items-center space-x-1">
                        <span className="font-caption text-xs text-muted-foreground capitalize">
                          {key}:
                        </span>
                        <span className="font-caption text-xs font-medium text-foreground">
                          {value}/5
                        </span>
                      </div>
                    )
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Show More */}
        {ratings?.length > 3 && (
          <button
            onClick={() => setShowAll(!showAll)}
            className="text-primary hover:text-primary/80 font-body text-sm font-medium transition-smooth"
          >
            {showAll ? 'Show Less' : `Show All ${ratings?.length} Ratings`}
          </button>
        )}
      </div>
    );
  }

  // Full view (non-compact)
  return (
    <div className="space-y-4">
      {/* Summary Header */}
      <div className="bg-card rounded-lg shadow-elevation-2 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-heading text-xl font-semibold text-foreground mb-2">
              Renter Ratings
            </h3>
            <div className="flex items-center space-x-3">
              <span className={`font-heading text-3xl font-bold ${getRatingColor(averageRating)}`}>
                {averageRating}
              </span>
              {renderStars(Math.round(averageRating))}
              <span className="font-body text-muted-foreground">
                ({ratings?.length} {ratings?.length === 1 ? 'rating' : 'ratings'})
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Individual Ratings */}
      <div className="space-y-3">
        {ratings?.map((rating) => (
          <div key={rating?.id} className="bg-card rounded-lg shadow-elevation-2 p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                {renderStars(rating?.overall_rating)}
                <span className="font-body font-medium text-foreground">
                  {rating?.overall_rating}/5
                </span>
              </div>
              <span className="font-caption text-xs text-muted-foreground">
                {formatDate(rating?.created_at)}
              </span>
            </div>

            {rating?.comment && (
              <p className="font-body text-foreground mb-3">
                {rating?.comment}
              </p>
            )}

            {rating?.category_ratings && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {Object.entries(rating?.category_ratings)?.map(([key, value]) => (
                  value > 0 && (
                    <div key={key} className="bg-muted rounded-lg p-2">
                      <p className="font-caption text-xs text-muted-foreground capitalize mb-1">
                        {key}
                      </p>
                      <div className="flex items-center space-x-1">
                        {renderStars(value)}
                      </div>
                    </div>
                  )
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default RenterProfileRatings;